import React, { useState, useEffect } from 'react';
import { Button, Spinner, Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faMusic, 
  faUser, 
  faTag, 
  faCompactDisc, 
  faClock, 
  faBuilding, 
  faCalendar,
  faBarcode,
  faVolumeUp,
  faWaveSquare,
  faHeadphones,
  faMicrochip,
  faPlay,
  faSearch,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';

const MusicIdData = ({ asset, showHeader = false }) => {
  const musicIdData = asset.metadata?.result || asset.metadata || asset.music_id || {};
  const nestedResult = musicIdData.result || {};

  // Extract metadata from various possible locations
  const title = musicIdData.title || nestedResult.title || asset.metadata?.title;
  const artist = musicIdData.artist || nestedResult.artist || asset.metadata?.artist;
  const label = musicIdData.label || nestedResult.label || asset.metadata?.label;
  const isrc = musicIdData.isrc || nestedResult.isrc || asset.metadata?.isrc;
  const vendor = musicIdData.vendor || nestedResult.vendor;
  const songLength = musicIdData.songLengthInSeconds || nestedResult.songLengthInSeconds;
  const mediaDuration = musicIdData.mediaDuration || nestedResult.mediaDuration;
  const contentCategory = musicIdData.contentCategory || nestedResult.contentCategory;
  const albumReleaseDate = musicIdData.albumReleaseDate || nestedResult.albumReleaseDate;
  const albumUPC = musicIdData.albumUPC || nestedResult.albumUPC;
  const audioCodec = musicIdData.audioCodec;
  const audioBitRate = musicIdData.audioBitRate;
  const audioSampleRate = musicIdData.audioSampleRate;
  const audioChannels = musicIdData.audioChannels;

  // Get download URLs
  const metadataUrl = musicIdData.file_metadata || nestedResult.file_metadata || asset.music_id?.file_metadata;
  const detailedResultUrl = musicIdData.content_id || nestedResult.content_id || nestedResult.result || asset.music_id?.content_id;

  const [detailedResults, setDetailedResults] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    const fetchDetailedResults = async () => {
      if (detailedResultUrl && typeof detailedResultUrl === 'string' && detailedResultUrl.startsWith('http')) {
        setLoadingDetails(true);
        setErrorDetails(null);
        try {
          const response = await fetch(detailedResultUrl);
          if (response.ok) {
            const data = await response.json();
            setDetailedResults(data);
          } else {
            setErrorDetails('Failed to fetch detailed results.');
            console.error('Failed to fetch detailed results', response.statusText);
          }
        } catch (error) {
          setErrorDetails('Error fetching detailed results.');
          console.error('Error fetching detailed results:', error);
        } finally {
          setLoadingDetails(false);
        }
      }
    };

    fetchDetailedResults();
  }, [detailedResultUrl]);

  // Helper function to create ISRC lookup URL
  const getIsrcLookupUrl = (isrcCode) => {
    if (!isrcCode) return null;
    return `https://isrcsearch.ifpi.org/?tab=%22code%22&isrcCode=%22${encodeURIComponent(isrcCode)}%22`;
  };

  // Helper function to render clickable ISRC
  const renderClickableIsrc = (isrcCode, className = '') => {
    if (!isrcCode) return null;
    const lookupUrl = getIsrcLookupUrl(isrcCode);
    return (
      <a 
        href={lookupUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className={`text-decoration-none ${className}`}
        style={{ color: '#d9d9d9' }}
        title="Look up ISRC in IFPI database"
      >
        {isrcCode}
        <FontAwesomeIcon 
          icon={faExternalLinkAlt} 
          className="ms-1" 
          style={{ fontSize: 'var(--font-sm)', color: 'var(--purple-accent)' }} 
        />
      </a>
    );
  };

  if (!title && !artist) {
    return null;
  }

  const InfoCard = ({ icon, label, value, color = 'var(--purple-accent)', isIsrc = false }) => {
    if (!value) return null;
    return (
      <div 
        className="p-3 rounded mb-3"
        style={{
          backgroundColor: 'rgba(179, 95, 208, 0.03)',
          border: '1px solid rgba(179, 95, 208, 0.15)',
          borderRadius: '8px'
        }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={icon} 
            className="me-3" 
            style={{ 
              color: color, 
              fontSize: 'var(--font-lg)',
              minWidth: '20px'
            }} 
          />
          <div className="flex-grow-1">
            <div className="small text-muted mb-1">{label}</div>
            <div style={{ color: '#d9d9d9', fontWeight: '500' }}>
              {isIsrc ? renderClickableIsrc(value) : value}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="mt-3 p-4 rounded" 
      style={{ 
        backgroundColor: 'rgba(179, 95, 208, 0.08)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(179, 95, 208, 0.2)',
        borderRadius: '12px',
        color: '#d9d9d9'
      }}
    >
      {showHeader && (
        <div className="mb-4 text-center">
          <FontAwesomeIcon 
            icon={faMusic} 
            className="mb-2" 
            style={{ 
              color: 'var(--purple-accent)', 
              fontSize: 'var(--font-3xl)' 
            }} 
          />
          <h5 className="mb-0" style={{ color: '#d9d9d9' }}>Music Identification</h5>
        </div>
      )}

      {/* Main Track Info */}
      <div 
        className="p-4 rounded mb-4"
        style={{
          backgroundColor: 'rgba(179, 95, 208, 0.05)',
          border: '1px solid rgba(179, 95, 208, 0.18)',
          borderRadius: '10px'
        }}
      >
        <div className="text-center mb-3">
          <h4 className="mb-2" style={{ color: '#d9d9d9', fontWeight: '600' }}>{title}</h4>
          <h6 className="mb-0 text-muted">by {artist}</h6>
        </div>
        
        <Row className="text-center">
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faBuilding} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Label</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{label}</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faTag} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Category</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{contentCategory}</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faClock} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Duration</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{songLength}s</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faUser} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Vendor</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{vendor}</div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Detailed Information Grid */}
      <Row>
        <Col md={6}>
          <InfoCard icon={faBarcode} label="ISRC Code" value={isrc} isIsrc={true} />
          {albumReleaseDate && albumReleaseDate !== '01-Jan-1900' && (
            <InfoCard icon={faCalendar} label="Release Date" value={albumReleaseDate} />
          )}
          {albumUPC && albumUPC !== 'Unspecified' && (
            <InfoCard icon={faCompactDisc} label="Album UPC" value={albumUPC} />
          )}
        </Col>
        <Col md={6}>
          {mediaDuration && (
            <InfoCard icon={faClock} label="Media Duration" value={`${Math.round(mediaDuration)}s`} />
          )}
          {audioCodec && (
            <InfoCard icon={faMicrochip} label="Audio Codec" value={audioCodec} />
          )}
          {audioBitRate && (
            <InfoCard icon={faVolumeUp} label="Bitrate" value={audioBitRate} />
          )}
          {audioSampleRate && (
            <InfoCard icon={faWaveSquare} label="Sample Rate" value={audioSampleRate} />
          )}
          {audioChannels && (
            <InfoCard icon={faHeadphones} label="Channels" value={audioChannels} />
          )}
        </Col>
      </Row>

      {/* Download Buttons */}
      <div className="text-center mt-4">
        {metadataUrl && (
          <Button 
            as="a" 
            href={metadataUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            variant="outline-light"
            className="beats-btn-outline me-3"
            size="sm"
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Download Metadata
          </Button>
        )}
        {detailedResultUrl && (
          <Button 
            as="a" 
            href={detailedResultUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="beats-btn-purple me-3"
            size="sm"
          >
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            View Detailed Results
          </Button>
        )}
        {isrc && (
          <Button 
            as="a" 
            href={getIsrcLookupUrl(isrc)} 
            target="_blank" 
            rel="noopener noreferrer" 
            variant="outline-light"
            className="beats-btn-outline"
            size="sm"
          >
            <FontAwesomeIcon icon={faBarcode} className="me-2" />
            ISRC Lookup
            <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" style={{ fontSize: 'var(--font-sm)' }} />
          </Button>
        )}
      </div>

      {/* Loading and Error States */}
      {loadingDetails && (
        <div className="mt-4 text-center p-3" style={{ backgroundColor: 'rgba(179, 95, 208, 0.04)', borderRadius: '8px' }}>
          <Spinner animation="border" size="sm" variant="light" /> 
          <span className="ms-2" style={{ color: '#d9d9d9' }}>Loading detailed matches...</span>
        </div>
      )}
      {errorDetails && (
        <div 
          className="mt-4 alert text-center" 
          style={{ 
            backgroundColor: 'rgba(220, 53, 69, 0.08)',
            border: '1px solid rgba(220, 53, 69, 0.2)',
            color: '#d9d9d9'
          }}
        >
          {errorDetails}
        </div>
      )}

      {/* Detailed Matches */}
      {detailedResults?.matches && detailedResults.matches.length > 0 && (
        <div className="mt-4">
          <div className="text-center mb-4">
            <h6 style={{ color: '#d9d9d9' }}>
              <FontAwesomeIcon icon={faSearch} className="me-2" style={{ color: 'var(--purple-accent)' }} />
              Detailed Matches ({detailedResults.matches.length})
            </h6>
          </div>
          
          {detailedResults.matches.map((match, index) => (
            <div 
              key={index} 
              className="mb-3 p-4 rounded"
              style={{
                backgroundColor: 'rgba(179, 95, 208, 0.04)',
                border: '1px solid rgba(179, 95, 208, 0.15)',
                borderRadius: '10px'
              }}
            >
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon 
                  icon={faPlay} 
                  className="me-3" 
                  style={{ 
                    color: 'var(--purple-accent)', 
                    fontSize: 'var(--font-lg)' 
                  }} 
                />
                <div>
                  <h6 className="mb-1" style={{ color: '#d9d9d9' }}>{match.metadata.Title}</h6>
                  <div className="small text-muted">Match #{index + 1}</div>
                </div>
                <div className="ms-auto">
                  <Badge 
                    bg="secondary" 
                    style={{ 
                      backgroundColor: 'var(--purple-accent)', 
                      fontSize: 'var(--font-sm)' 
                    }}
                  >
                    {match.matchType}
                  </Badge>
                </div>
              </div>
              
              <Row>
                <Col md={6}>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                    <span className="text-muted">Artist:</span> 
                    <span className="ms-1" style={{ color: '#d9d9d9' }}>{match.metadata.Artist}</span>
                  </div>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" />
                    <span className="text-muted">Label:</span> 
                    <span className="ms-1" style={{ color: '#d9d9d9' }}>{match.metadata.Label}</span>
                  </div>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faBarcode} className="me-2 text-muted" />
                    <span className="text-muted">ISRC:</span> 
                    <span className="ms-1">{renderClickableIsrc(match.metadata.ISRC)}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                    <span className="text-muted">Match Duration:</span> 
                    <span className="ms-1" style={{ color: '#d9d9d9' }}>{match.matchDurationInUnknown}s</span>
                  </div>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faPlay} className="me-2 text-muted" />
                    <span className="text-muted">Match Time:</span> 
                    <span className="ms-1" style={{ color: '#d9d9d9' }}>{match.matchTimeInUnknown}s</span>
                  </div>
                  <div className="small mb-2">
                    <FontAwesomeIcon icon={faCompactDisc} className="me-2 text-muted" />
                    <span className="text-muted">Reference Time:</span> 
                    <span className="ms-1" style={{ color: '#d9d9d9' }}>{match.matchTimeInReference}s</span>
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicIdData; 